import { Hash, MessageCircle, Users } from '@lucide/vue';
import { readShellAccount } from '@blpde/connect';
import type { NxCommand } from '@blpde/nx-ui';
import type { ChatChannel } from '@/chat/chatApi';
import type { CommandFactory } from './commands';

type Me = { email?: string | null; name?: string | null } | null;

/**
 * A channel's caption: its name, or for a direct message the other members'
 * names (the list API has no display name for those).
 */
export function chatChannelLabel(channel: ChatChannel, me: Me): string {
  if (channel.type !== 'direct') return channel.name?.trim() || `#${channel.id}`;
  const others = (channel.members ?? [])
    .map((member) => member.account)
    .filter((account): account is NonNullable<typeof account> => !!account)
    .filter((account) =>
      me?.email && account.email ? account.email !== me.email : account.name !== me?.name
    )
    .map((account) => account.name?.trim() || account.email || account.address || '')
    .filter(Boolean);
  return others.join(', ') || channel.name?.trim() || `#${channel.id}`;
}

/**
 * Chat's app-wide palette entry (I-2348): open one of the caller's channels,
 * listed on demand from the chat module. „Neue Nachricht an …“ lives with the
 * shell's DM panel (shellAppCommands.ts); the open channel's actions join the
 * palette while Chat is open (frontend/chat).
 */
export const chatCommands: CommandFactory = ({ router, t, appLabel: chat }) => {
  const typeHint = (channel: ChatChannel) =>
    channel.type === 'direct'
      ? t('chat.direct_message')
      : channel.type === 'group'
        ? t('chat.type_group')
        : t('chat.type_public');
  return [
    {
      id: 'chat.openChannel',
      label: `${chat}: ${t('chat.command.openChannel')}`,
      group: chat,
      app: 'chat',
      icon: Hash,
      category: 'navigation',
      keywords: ['chat', 'channel', 'direktnachricht', 'direct message', 'dm', 'gruppe'],
      run: () => {
        void router.push('/chat');
      },
      children: async () => {
        const { listChannels } = await import('@/chat/chatApi');
        const me = readShellAccount();
        return (await listChannels()).map((channel): NxCommand => {
          const href = `/chat/channels/${channel.id}`;
          return {
            id: `chat.openChannel.${channel.id}`,
            label: chatChannelLabel(channel, me),
            hint: typeHint(channel),
            icon: channel.type === 'direct' ? MessageCircle : channel.type === 'group' ? Users : Hash,
            app: 'chat',
            category: 'navigation',
            href,
            transient: true,
            run: () => {
              void router.push(href);
            },
          };
        });
      },
    },
  ];
};
